/**
 * Fairness Flag Store (Gap 3)
 *
 * Manages process-fairness flags raised by participants and their resolution by
 * facilitators / cycle owners. Supabase is the source of truth; local state is a
 * cache reconstructible from the DB (Build Contract §2.1).
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  isSupabaseAvailable,
  generateUUID,
} from '../../lib/supabaseSync';
import { isUuid } from '../../lib/isUuid';
import type {
  FairnessFlag,
  FairnessConcernType,
  FairnessFlagStatus,
} from '../../types/prioritization/fairnessFlag';

const TABLE = 'session_fairness_flags';

function rowToFlag(row: Record<string, unknown>): FairnessFlag {
  return {
    id: row.id as string,
    sessionId: row.session_id as string,
    toolSessionId: (row.tool_session_id as string | null) ?? null,
    workspaceId: row.workspace_id as string,
    raisedBy: row.raised_by as string,
    raisedByName: (row.raised_by_name as string) ?? '',
    concernType: (row.concern_type as FairnessConcernType) ?? 'other',
    note: (row.note as string | null) ?? null,
    status: (row.status as FairnessFlagStatus) ?? 'open',
    acknowledgedBy: (row.acknowledged_by as string | null) ?? null,
    acknowledgedByName: (row.acknowledged_by_name as string | null) ?? null,
    acknowledgedAt: (row.acknowledged_at as string | null) ?? null,
    resolutionNote: (row.resolution_note as string | null) ?? null,
    createdAt: (row.created_at as string) ?? new Date().toISOString(),
    updatedAt: (row.updated_at as string) ?? new Date().toISOString(),
  };
}

interface FairnessFlagState {
  flags: FairnessFlag[];
  isLoading: boolean;
  error: string | null;

  fetchForSession: (sessionId: string) => Promise<void>;

  raiseFlag: (params: {
    sessionId: string;
    toolSessionId?: string | null;
    raisedBy: string;
    raisedByName: string;
    concernType: FairnessConcernType;
    note?: string;
  }) => Promise<{ flag?: FairnessFlag; error?: string }>;

  resolveFlag: (params: {
    flagId: string;
    status: Extract<FairnessFlagStatus, 'acknowledged' | 'resolved' | 'dismissed'>;
    actorId: string;
    actorName: string;
    resolutionNote?: string;
  }) => Promise<{ error?: string }>;

  getFlagsForSession: (sessionId: string) => FairnessFlag[];
  getOpenCountForSession: (sessionId: string) => number;
}

export const useFairnessFlagStore = create<FairnessFlagState>()((set, get) => ({
  flags: [],
  isLoading: false,
  error: null,

  fetchForSession: async (sessionId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    set({ isLoading: true, error: null });
    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: false });
    if (error) {
      set({ isLoading: false, error: error.message });
      return;
    }
    const fetched = (data ?? []).map(r => rowToFlag(r as Record<string, unknown>));
    set(state => {
      const others = state.flags.filter(f => f.sessionId !== sessionId);
      return { flags: [...others, ...fetched], isLoading: false };
    });
  },

  raiseFlag: async ({ sessionId, toolSessionId, raisedBy, raisedByName, concernType, note }) => {
    if (!isSupabaseAvailable()) {
      return { error: 'Database not available — your concern was not saved.' };
    }
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { error: 'Database client not available.' };

    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return { error: 'No workspace context.' };

    const now = new Date().toISOString();
    const flag: FairnessFlag = {
      id: generateUUID(),
      sessionId,
      toolSessionId: toolSessionId ?? null,
      workspaceId,
      raisedBy,
      raisedByName,
      concernType,
      note: note?.trim() ? note.trim() : null,
      status: 'open',
      createdAt: now,
      updatedAt: now,
    };

    const { error } = await supabase.from(TABLE).insert({
      id: flag.id,
      session_id: flag.sessionId,
      tool_session_id: flag.toolSessionId,
      workspace_id: flag.workspaceId,
      raised_by: flag.raisedBy,
      raised_by_name: flag.raisedByName,
      concern_type: flag.concernType,
      note: flag.note,
      status: 'open',
      created_at: now,
      updated_at: now,
    });

    if (error) {
      return { error: error.message };
    }

    set(state => ({ flags: [flag, ...state.flags] }));
    return { flag };
  },

  resolveFlag: async ({ flagId, status, actorId, actorName, resolutionNote }) => {
    if (!isSupabaseAvailable()) return { error: 'Database not available.' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { error: 'Database client not available.' };
    // `acknowledged_by` is a uuid column. The user store's default id is the
    // literal 'unknown', and the panel once substituted 'facilitator'; either
    // is rejected by Postgres (22P02) and used to be swallowed (#621).
    if (!isUuid(actorId)) {
      return { error: 'No signed-in user to record this action against. Sign in again and retry.' };
    }

    const now = new Date().toISOString();
    const patch: Record<string, unknown> = {
      status,
      acknowledged_by: actorId,
      acknowledged_by_name: actorName,
      acknowledged_at: now,
      resolution_note: resolutionNote?.trim() ? resolutionNote.trim() : null,
      updated_at: now,
    };

    // Ask for the row back: an UPDATE that matches nothing (row gone, or RLS
    // filtered it out) returns no error, and marking the flag resolved locally
    // on that would show a closed loop that never closed.
    const { data, error } = await supabase.from(TABLE).update(patch).eq('id', flagId).select('*');
    if (error) return { error: error.message };
    const row = (data ?? [])[0] as Record<string, unknown> | undefined;
    if (!row) {
      return { error: 'The concern was not updated — it may have been removed, or you may not have access to it in this workspace.' };
    }

    const updated = rowToFlag(row);
    set(state => ({
      flags: state.flags.map(f => (f.id === flagId ? updated : f)),
    }));
    return {};
  },

  getFlagsForSession: (sessionId) => get().flags.filter(f => f.sessionId === sessionId),

  getOpenCountForSession: (sessionId) =>
    get().flags.filter(f => f.sessionId === sessionId && f.status === 'open').length,
}));
